import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_URL, getAuthHeaders, readJsonSafe, readJsonOrThrow } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { showToast } from "./errors";
import "./PagePermissionsModal.css";

/**
 * "Who can see this page?" — admin-only audit of the permissions governing the
 * page behind the modal, with grant/revoke in place.
 *
 * Reads GET /admin/permissions-overview/page?path=… which resolves the route to
 * its permissions (backend utils/pagePermissionMap.js) and returns, for each,
 * only the users who actually hold it. Writes reuse the same
 * PUT /admin/users/:id/permissions the Settings ▸ Permissions page uses — one
 * write path, so effective access here always matches what the gates enforce.
 */

const ROLE_LABELS = {
  admin: "Admin",
  technician_manager: "Technician Manager",
  technician: "Technician",
  sales_manager: "Sales Manager",
  service_writer: "Service Writer",
  sales_support: "Sales Support",
  marketing_manager: "Marketing Manager",
  field_representative: "Field Representative",
  event_coordinator: "Event Coordinator",
  creative: "Creative",
};

const SOURCE_LABELS = {
  override: "Custom",
  company_default: "Role default",
  factory_default: "Role default",
};

export default function PagePermissionsModal({ path, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [busyKey, setBusyKey] = useState(null);
  const [search, setSearch] = useState({});
  const { user: currentUser } = useAuth();
  const actorIsSuperAdmin = Boolean(currentUser?.is_super_admin);

  // Mirror of middleware/userAdminGuards.js guardUserTarget({privilegeChange:true}),
  // which every write here passes through. Returns the reason a target is
  // off-limits, or null. Without this the buttons render and only ever 403.
  const protectionFor = (u) => {
    const isSelf = String(u?.id) === String(currentUser?.id);
    if (u?.isSuperAdmin && !isSelf) {
      return "This is the super admin account — only they can change it.";
    }
    // The guard checks the primary role only, so this mirror does too.
    if (u?.role === "admin" && !isSelf && !actorIsSuperAdmin) {
      return "Only the super admin can change another administrator's permissions.";
    }
    return null;
  };

  const load = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/admin/permissions-overview/page?path=${encodeURIComponent(path)}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) {
        const b = await readJsonSafe(res);
        throw new Error(
          b.code === "PAGE_NOT_MAPPED"
            ? "This page doesn't have permissions mapped to it yet."
            : b.error || `Couldn't load permissions (HTTP ${res.status}).`
        );
      }
      const body = await readJsonOrThrow(res, "Couldn't load permissions");
      setData(body);
      setError("");
      // Open the permissions that actually gate this page; leave the wider
      // area list collapsed so the modal isn't a wall on first open.
      setExpanded(
        new Set(body.permissions.filter((p) => p.group === "primary").map((p) => p.key))
      );
    } catch (e) {
      setError(e.message);
    }
  }, [path]);

  useEffect(() => { load(); }, [load]);

  // Esc closes, matching the app's other modals.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // value: for boolean permissions true/false; for enum permissions one of
  // perm.options (the server rejects a bare true/false there); null always
  // clears the override and falls back to the role default.
  const setPermission = async (perm, user, value, verb) => {
    setBusyKey(`${perm.key}:${user.id}`);
    try {
      // Granting a child permission does nothing on its own — the server gates
      // it on its parents (permissions-catalog.js PERMISSION_DEPENDENCIES), so
      // "give access" has to grant the whole path or it silently no-ops.
      const overrides = { [perm.key]: value };
      const alsoGranted = [];
      if (value !== false && value !== null) {
        for (const dep of perm.dependsOn || []) {
          if (!data?.depHolders?.[dep.key]?.includes(user.id)) {
            overrides[dep.key] = true;
            alsoGranted.push(dep.label);
          }
        }
      }
      const res = await fetch(`${API_URL}/admin/users/${user.id}/permissions`, {
        method: "PUT",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ overrides }),
      });
      if (!res.ok) {
        const b = await readJsonSafe(res);
        throw new Error(b.error || `Update failed (HTTP ${res.status}).`);
      }
      showToast.success(
        `${verb} ${perm.label} for ${user.name}` +
          (alsoGranted.length ? ` (also granted ${alsoGranted.join(", ")})` : "")
      );
      await load();
    } catch (e) {
      showToast.error(e.message || "Couldn't update the permission.");
    } finally {
      setBusyKey(null);
    }
  };

  const holdersById = useMemo(() => {
    const m = {};
    for (const p of data?.permissions || []) m[p.key] = new Set(p.users.map((u) => u.id));
    return m;
  }, [data]);

  const grantCandidates = (perm) => {
    const q = (search[perm.key] || "").trim().toLowerCase();
    if (!q) return [];
    const have = holdersById[perm.key] || new Set();
    return (data?.allUsers || [])
      .filter((u) => !have.has(u.id))
      .filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q) ||
          (ROLE_LABELS[u.role] || u.role).toLowerCase().includes(q)
      )
      .slice(0, 6);
  };

  const renderGroup = (group, heading, hint) => {
    const perms = (data?.permissions || []).filter((p) => p.group === group);
    if (!perms.length) return null;
    return (
      <>
        <div className="ppm-group-head">
          {heading}
          <span className="ppm-group-hint">{hint}</span>
        </div>
        {perms.map((perm) => {
          const open = expanded.has(perm.key);
          return (
            <div key={perm.key} className={`ppm-perm${open ? " open" : ""}`}>
              <button type="button" className="ppm-perm-head" onClick={() => toggle(perm.key)}>
                <span className="ppm-chev" aria-hidden>{open ? "▾" : "▸"}</span>
                <span className="ppm-perm-main">
                  <span className="ppm-perm-label">{perm.label}</span>
                  <code className="ppm-perm-key">{perm.key}</code>
                  {perm.description && (
                    <span className="ppm-perm-desc">{perm.description}</span>
                  )}
                  {perm.dependsOn?.length > 0 && (
                    // Why a list can be shorter than expected: this permission
                    // does nothing on its own, so holders of it who lack the
                    // parent don't appear here.
                    <span className="ppm-perm-requires">
                      Requires {perm.dependsOn.map((d) => d.label).join(" › ")}
                    </span>
                  )}
                </span>
                <span className={`ppm-count${perm.holderCount === 0 ? " zero" : ""}`}>
                  {perm.holderCount} {perm.holderCount === 1 ? "user" : "users"}
                </span>
              </button>

              {open && (
                <div className="ppm-perm-body">
                  {perm.users.length === 0 ? (
                    <p className="ppm-empty">Nobody currently has this.</p>
                  ) : (
                    <ul className="ppm-user-list">
                      {perm.users.map((u) => (
                        <li key={u.id} className={`ppm-user${u.active ? "" : " inactive"}`}>
                          <span className="ppm-user-main">
                            <span className="ppm-user-name">
                              {u.name}
                              {!u.active && <span className="ppm-muted"> · disabled</span>}
                            </span>
                            <span className="ppm-user-meta">
                              {ROLE_LABELS[u.role] || u.role}
                              {" · "}
                              <span className={u.source === "override" ? "ppm-src-custom" : ""}>
                                {SOURCE_LABELS[u.source] || u.source}
                              </span>
                            </span>
                          </span>
                          <span className="ppm-user-actions">
                            {protectionFor(u) ? (
                              <span className="ppm-locked" title={protectionFor(u)}>
                                Protected
                              </span>
                            ) : (
                              <>
                                {u.source === "override" && (
                                  <button
                                    type="button"
                                    className="ppm-link"
                                    disabled={busyKey === `${perm.key}:${u.id}`}
                                    title="Remove the custom setting — fall back to their role default"
                                    onClick={() => setPermission(perm, u, null, "Reset")}
                                  >
                                    Reset
                                  </button>
                                )}
                                {perm.type === "enum" ? (
                                  // Scope permissions have no "off" value — every
                                  // option still grants access — so the control is
                                  // the scope itself, not a revoke.
                                  <select
                                    className="ppm-scope"
                                    value={String(u.value)}
                                    disabled={busyKey === `${perm.key}:${u.id}`}
                                    title="Change how much this person can see"
                                    onChange={(e) =>
                                      setPermission(perm, u, e.target.value, "Updated")
                                    }
                                  >
                                    {/* A role default can hold a non-option value
                                        (e.g. a stray `true`). Surface it instead of
                                        silently displaying the first option. */}
                                    {!(perm.options || []).some(
                                      (o) => String(o.value) === String(u.value)
                                    ) && (
                                      <option value={String(u.value)}>
                                        {String(u.value)} (unrecognized)
                                      </option>
                                    )}
                                    {(perm.options || []).map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <button
                                    type="button"
                                    className="ppm-link danger"
                                    disabled={busyKey === `${perm.key}:${u.id}`}
                                    title={
                                      u.source === "override"
                                        ? "Remove this permission"
                                        : "They have this from their role — this denies it for them individually"
                                    }
                                    onClick={() => setPermission(perm, u, false, "Revoked")}
                                  >
                                    {busyKey === `${perm.key}:${u.id}` ? "Saving…" : "Revoke"}
                                  </button>
                                )}
                              </>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="ppm-grant">
                    <input
                      type="text"
                      className="ppm-grant-input"
                      placeholder="Search a user to give access…"
                      value={search[perm.key] || ""}
                      onChange={(e) =>
                        setSearch((s) => ({ ...s, [perm.key]: e.target.value }))
                      }
                    />
                    {grantCandidates(perm).map((u) => {
                      const locked = protectionFor(u);
                      const busy = busyKey === `${perm.key}:${u.id}`;
                      const give = (value) => {
                        setSearch((s) => ({ ...s, [perm.key]: "" }));
                        setPermission(perm, u, value, "Granted");
                      };
                      return (
                        <div key={u.id} className="ppm-grant-row">
                          <span>
                            {u.name}
                            <span className="ppm-muted"> · {ROLE_LABELS[u.role] || u.role}</span>
                          </span>
                          {locked ? (
                            <span className="ppm-locked" title={locked}>
                              Protected
                            </span>
                          ) : perm.type === "enum" ? (
                            // Enum grants must name a scope — the server rejects a
                            // bare true — so offer the options directly.
                            <span className="ppm-grant-opts">
                              {(perm.options || []).map((o) => (
                                <button
                                  key={o.value}
                                  type="button"
                                  className="ppm-grant-add"
                                  disabled={busy}
                                  onClick={() => give(o.value)}
                                >
                                  {o.label}
                                </button>
                              ))}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="ppm-grant-add"
                              disabled={busy}
                              onClick={() => give(true)}
                            >
                              {busy ? "Saving…" : "Give access"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {(search[perm.key] || "").trim() && grantCandidates(perm).length === 0 && (
                      <p className="ppm-empty">No other users match.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content modal-form-shell ppm-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ppm-title"
      >
        <div className="modal-header">
          <div>
            <h2 id="ppm-title">Who can see this page?</h2>
            {data?.page && (
              <p className="ppm-subtitle">
                {data.page.title}
                {data.page.subtitle ? ` ▸ ${data.page.subtitle}` : ""}
              </p>
            )}
          </div>
          <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="modal-body">
          {error ? (
            <p className="ppm-error">{error}</p>
          ) : !data ? (
            <p className="ppm-empty">Loading…</p>
          ) : (
            <>
              {renderGroup("primary", "Controls this page", "who gets in at all")}
              {renderGroup("related", "Related permissions", "features and data on this page")}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
